import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";
import { XAutoComplete } from "share-components/mui-wrap/auto-complete/x-auto-complete";
import {
  ProductGroupSelectionKey,
  ProductGroupSelectionModel,
  useProductGroupSelection,
} from "./use-product-group-selection";

type ProductGroupSelectionProps = {
  dealingNameSelected: string;
  nameSelected: string;
  codeSelected: string;
  barcodeSelected?: string;
  setSelectionProp: (prop: ProductGroupSelectionKey, value: string) => void;
  visibleFields?: ProductGroupSelectionKey[];
  searchWhenRender?: boolean;
};

const DEFAULT_VISIBLE_FIELDS: ProductGroupSelectionKey[] = [
  "dealingName",
  "name",
  "code",
  "barcode",
];

export const ProductGroupSelection = observer(
  ({
    dealingNameSelected,
    nameSelected,
    codeSelected,
    barcodeSelected = "",
    setSelectionProp,
    visibleFields = DEFAULT_VISIBLE_FIELDS,
    searchWhenRender = false,
  }: ProductGroupSelectionProps) => {
    const selection = useMemo<ProductGroupSelectionModel>(
      () => ({
        dealingName: dealingNameSelected,
        name: nameSelected,
        code: codeSelected,
        barcode: barcodeSelected,
      }),
      [barcodeSelected, codeSelected, dealingNameSelected, nameSelected],
    );

    const {
      searchDealingNameOptions,
      searchNameOptions,
      searchCodeOptions,
      searchBarcodeOptions,
      getValidSelectionAfterChange,
    } = useProductGroupSelection(selection);

    const selectionChangedHandler = useCallback(
      (key: ProductGroupSelectionKey, value: string) => {
        const nextSelection = getValidSelectionAfterChange(key, value);

        (Object.keys(nextSelection) as ProductGroupSelectionKey[]).forEach(
          (selectionKey) => {
            if (nextSelection[selectionKey] === selection[selectionKey]) return;
            setSelectionProp(selectionKey, nextSelection[selectionKey]);
          },
        );
      },
      [getValidSelectionAfterChange, selection, setSelectionProp],
    );

    return (
      <>
        {visibleFields.includes("dealingName") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Tên giao dịch"
              value={dealingNameSelected}
              searchFunc={searchDealingNameOptions}
              searchOnFocus={true}
              searchWhenRender={searchWhenRender}
              onValueChanged={(v) => {
                selectionChangedHandler("dealingName", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("name") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Tên hàng"
              value={nameSelected}
              searchFunc={searchNameOptions}
              searchOnFocus={true}
              searchWhenRender={searchWhenRender}
              onValueChanged={(v) => {
                selectionChangedHandler("name", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("code") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Mã hàng"
              value={codeSelected}
              searchFunc={searchCodeOptions}
              searchOnFocus={true}
              searchWhenRender={searchWhenRender}
              onValueChanged={(v) => {
                selectionChangedHandler("code", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("barcode") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Mã barcode"
              value={barcodeSelected}
              searchFunc={searchBarcodeOptions}
              searchOnFocus={true}
              searchWhenRender={searchWhenRender}
              onValueChanged={(v) => {
                selectionChangedHandler("barcode", v?.value ?? "");
              }}
            />
          </Box>
        )}
      </>
    );
  },
);
